import { Placement } from "@floating-ui/dom";
import { createFocusTrap } from "focus-trap";
import float, { FloatOptions } from "../../helpers/float";
import ApplicationController from "../application_controller";
import { autofocus } from "../../helpers/dom";

export default class DropdownController extends ApplicationController {
  static targets = ["container", "template"];
  static values = { hideClass: String };

  declare readonly containerTarget: HTMLElement;
  declare readonly templateTarget: HTMLTemplateElement;
  declare readonly hasContainerTarget: boolean;
  declare readonly hasTemplateTarget: boolean;
  declare hideClassValue: string;
  active: boolean = false;
  anchor?: HTMLElement;
  unfloatFn?: () => void;
  focustrap = createFocusTrap(this.container, {
    allowOutsideClick: true, // Allow outside clicks to trigger mouseClose mousedown event (see App.rb)
    fallbackFocus: this.container, // Fallback to container when dropdown has no focusable elements

    // rely on autofocus to focus the first focusable element. Otherwise, this will focus the container
    initialFocus: false,

    // Disable ESC auto-deactivation to prevent double deactivation.
    escapeDeactivates: false,
  });

  toggle(event: StimulusEvent<{ placement: Placement }>) {
    event.preventDefault();
    event.stopPropagation();
    const { placement } = event.params;
    const anchor = event.currentTarget;

    if (this.active) {
      this.hide();
      return;
    }

    if (this.container === this.globalContainer) {
      const isNestedDropdown = this.container.contains(anchor);
      this.container.innerHTML = this.templateTarget.innerHTML;
      // triggering a dropdown while inside global dropdown container will replace content, and not refloat
      if (isNestedDropdown) {
        return;
      }
    }

    this.show(anchor, { placement });
  }

  show(anchor: HTMLElement, options: FloatOptions = {}) {
    if (this.active) return;
    this.anchor = anchor;
    this.unfloatFn = float(this.anchor, this.container, options);
    this.container.removeAttribute("hidden");
    // wysiwyg linkMenu anchors to text in the editor, so there is no aria-expanded attribute
    // changing the node's attribute triggers updates in the editor
    if (this.anchor.hasAttribute("aria-expanded")) {
      this.anchor.setAttribute("aria-expanded", "true");
    }
    this.dispatch("show");
    this.active = true;
    // Wait for next frame since DOM changes (classes/styles) don't render until current JS execution completes.
    // This ensures focus trap activates after the dropdown is visible to prevent focus issues.
    requestAnimationFrame(() => {
      this.focustrap.activate();
      autofocus(this.container, { preventScroll: true });
    });
  }

  hide() {
    if (!this.active) return;
    this.container.toggleAttribute("hidden", true);
    this.unfloatFn?.();
    this.focustrap.deactivate();
    // wysiwyg linkMenu anchors to text in the editor, so there is no aria-expanded attribute
    // changing the node's attribute triggers updates in the editor
    if (this.anchor?.hasAttribute("aria-expanded")) {
      this.anchor.setAttribute("aria-expanded", "false");
    }
    this.dispatch("hide");
    this.active = false;
  }

  mouseClose(event: StimulusEvent) {
    if (!this.active) return;
    if (this.anchor?.contains(event.target) || this.container.contains(event.target)) {
      return;
    }
    this.hide();
  }

  get globalContainer() {
    return document.querySelector("#global-dropdown-container")! as HTMLElement;
  }

  get container() {
    if (this.hasContainerTarget) {
      return this.containerTarget;
    }
    if (this.hasTemplateTarget) {
      return this.globalContainer;
    }
    throw new Error("Dropdown needs a container or template target");
  }
}
